import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Slide,
    IconButton,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchEmpresas } from '../../services/empleados.api';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const OSForm = ({ open, handleClose, handleSubmit, initialData }) => {
    const [formData, setFormData] = useState({
        nombre: '',
        empresa_id: '',
        plan: '',
        nro_os: '',
        telefono: '',
        sisven_id: ''
    });
    const [empresas, setEmpresas] = useState([]);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const loadEmpresas = async () => {
            try {
                const data = await fetchEmpresas();
                setEmpresas(data || []);
            } catch (error) {
                console.error("Error loading empresas for OSForm:", error);
            }
        };
        loadEmpresas();
    }, []);

    useEffect(() => {
        if (initialData) {
            setFormData({
                nombre: initialData.nombre || '',
                empresa_id: initialData.empresa_id || '',
                plan: initialData.plan || '',
                nro_os: initialData.nro_os || '',
                telefono: initialData.telefono || '',
                sisven_id: initialData.sisven_id || initialData.id_sisven || ''
            });
        } else {
            setFormData({
                nombre: '',
                empresa_id: '',
                plan: '',
                nro_os: '',
                telefono: '',
                sisven_id: ''
            });
        }
        setErrors({});
    }, [initialData, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    const validate = () => {
        const newErrors = {};
        
        // Nombre
        if (!formData.nombre.trim()) {
            newErrors.nombre = "El nombre es obligatorio";
        } else if (formData.nombre.trim().length > 100) {
            newErrors.nombre = "El nombre no puede exceder los 100 caracteres";
        }

        // Empresa
        if (!formData.empresa_id) {
            newErrors.empresa_id = "La empresa es obligatoria";
        }

        // Plan
        if (!formData.plan.trim()) {
            newErrors.plan = "El plan es obligatorio";
        } else if (formData.plan.trim().length > 20) {
            newErrors.plan = "El plan no puede exceder los 20 caracteres";
        }
        
        // Nro OS
        if (!formData.nro_os) {
            newErrors.nro_os = "El número es obligatorio";
        } else if (isNaN(formData.nro_os)) {
            newErrors.nro_os = "Debe ser un valor numérico";
        } else if (formData.nro_os.toString().length > 20) {
            newErrors.nro_os = "No puede exceder los 20 dígitos";
        }

        // Telefono
        if (!formData.telefono.trim()) {
            newErrors.telefono = "El teléfono es obligatorio";
        } else if (!/^\+?[\d\s-]+$/.test(formData.telefono)) {
            newErrors.telefono = "Formato de teléfono inválido";
        } else if (formData.telefono.trim().length > 20) {
            newErrors.telefono = "No puede exceder los 20 caracteres";
        }

        // Sisven ID
        if (!formData.sisven_id) {
            newErrors.sisven_id = "El ID Sisven es obligatorio";
        } else if (isNaN(formData.sisven_id)) {
            newErrors.sisven_id = "Debe ser un valor numérico";
        } else if (formData.sisven_id.toString().length > 20) {
            newErrors.sisven_id = "No puede exceder los 20 dígitos";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            handleSubmit(formData);
        }
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                style: { borderRadius: 15, padding: '10px' }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {initialData ? 'Editar Obra Social' : 'Nueva Obra Social'}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <form onSubmit={onSubmit}>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                name="nombre"
                                label="Nombre de la Obra Social"
                                fullWidth
                                variant="outlined"
                                value={formData.nombre}
                                onChange={handleChange}
                                error={!!errors.nombre}
                                helperText={errors.nombre}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth error={!!errors.empresa_id}>
                                <InputLabel>Empresa</InputLabel>
                                <Select
                                    name="empresa_id"
                                    value={formData.empresa_id}
                                    label="Empresa"
                                    onChange={handleChange}
                                >
                                    {empresas.map(emp => (
                                        <MenuItem key={emp.idEmpresa} value={emp.idEmpresa}>
                                            {emp.empresa}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.empresa_id && (
                                    <Box sx={{ color: 'error.main', fontSize: '0.75rem', px: 2, mt: 0.5 }}>
                                        {errors.empresa_id}
                                    </Box>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="plan"
                                label="Plan"
                                fullWidth
                                variant="outlined"
                                value={formData.plan}
                                onChange={handleChange}
                                error={!!errors.plan}
                                helperText={errors.plan}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="nro_os"
                                label="Número OS"
                                fullWidth
                                variant="outlined"
                                value={formData.nro_os}
                                onChange={handleChange}
                                error={!!errors.nro_os}
                                helperText={errors.nro_os}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="telefono"
                                label="Teléfono"
                                fullWidth
                                variant="outlined"
                                value={formData.telefono}
                                onChange={handleChange}
                                error={!!errors.telefono}
                                helperText={errors.telefono}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="sisven_id"
                                label="ID Sisven"
                                fullWidth
                                variant="outlined"
                                value={formData.sisven_id}
                                onChange={handleChange}
                                error={!!errors.sisven_id}
                                helperText={errors.sisven_id}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose} color="inherit" variant="text">
                        Cancelar
                    </Button>
                    <Button type="submit" variant="contained" color="primary" sx={{ boxShadow: 3 }}>
                        {initialData ? 'Actualizar' : 'Guardar'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default OSForm;
